// One key per intended submission of a credential share or request. The server stores the result
// of a keyed submission under that key, so a repeat of it is answered with the first result instead
// of making a second share or request.
//
// Identical submissions still waiting for an answer are one submission: they share one call and
// one key. After an answer - success or a refusal - the key is dropped, so doing the same thing again
// is a new submission. After no answer, or a server error, it is kept, so trying again is recognised
// as the same submission. Keys live in this tab only; a reload starts afresh.

const inFlight = new Map();
const kept = new Map();

const hex = bytes => Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');

const randomBytes = n => {
  const bytes = new Uint8Array(n);
  const crypto = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (crypto && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < n; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  return bytes;
};

// A version 4 UUID, the shape the server accepts.
const newKey = () => {
  const crypto = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (crypto && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const b = randomBytes(16);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = hex(b);
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
};

// The same details in any key order are the same submission.
const stable = value => {
  if (Array.isArray(value)) return `[${value.map(stable).join(',')}]`;
  if (value && typeof value === 'object') {
    return `{${Object.keys(value)
      .sort()
      .filter(k => value[k] !== undefined)
      .map(k => `${JSON.stringify(k)}:${stable(value[k])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value);
};

// An answer from the server ends the submission; no answer, or a server error, does not.
const wasAnswered = error => typeof error?.status === 'number' && error.status < 500;

export const keyedSubmission = (scope, payload, send) => {
  const fingerprint = `${scope}|${stable(payload)}`;
  const waiting = inFlight.get(fingerprint);
  if (waiting) return waiting;

  const submissionKey = kept.get(fingerprint) || newKey();
  kept.set(fingerprint, submissionKey);
  const call = Promise.resolve()
    .then(() => send({ ...payload, submissionKey }))
    .then(
      response => {
        inFlight.delete(fingerprint);
        kept.delete(fingerprint);
        return response;
      },
      error => {
        inFlight.delete(fingerprint);
        if (wasAnswered(error)) kept.delete(fingerprint);
        throw error;
      }
    );
  inFlight.set(fingerprint, call);
  return call;
};

export const resetSubmissionKeysForTests = () => {
  inFlight.clear();
  kept.clear();
};
